import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaLongArrowAltLeft, FaSlidersH, FaStore, FaTags, FaChartLine } from "react-icons/fa";
import { GrAscend } from "react-icons/gr";
import useElakaySwiper from "./swiperSettings/useElakaySwiper";
import { elakayAutoText } from "../pageInside/dataElakay";
import { ELAKAY_SITE_URL } from "../../config";
import { buildMobilePrimaryNavigation } from "../Layout/primaryNavigation";
import { canDisplayMenuSwiperItem, resolveMenuSwiperItemText } from "./menuSwiperCatalog";
import { resolveMenuSliderButtons, resolveMenuSliderItems, resolveMenuSliderPageContext } from "./menuSliderPlacement";
import { hasInAppHistory } from "../auth/navigationHistory";
import "./menubarSlider.css";

// "Back" never leaves Elakay. A SPA navigation does not update
// document.referrer, so the router's own history index decides whether an
// in-app page precedes this one; otherwise (direct link, new tab, refresh
// after an external site) the visitor goes to Home inside the app.
const goBackWith = (navigate) => () => {
  if (hasInAppHistory()) {
    navigate(-1);
    return;
  }
  navigate("/", { replace: true });
};

// Sécurité : le bouton "retour" ne doit jamais quitter la plateforme Elakay.
// On n'accepte que des chemins internes relatifs à la racine ("/xxx"). Tout
// lien avec un schéma (http:, https:, javascript:, mailto:, ...) ou une URL
// protocol-relative ("//external.com") est rejeté → fallback sur goBack().
const isInternalElakayPath = (link) => {
  if (typeof link !== "string" || !link.trim()) return false;

  try {
    const elakayHost = new URL(ELAKAY_SITE_URL).hostname;

    // Les chemins relatifs sont toujours internes
    if (link.startsWith("/")) return true;

    // Convertit les liens relatifs en URL absolue
    const url = new URL(link, ELAKAY_SITE_URL);

    return (
      url.hostname === elakayHost ||
      url.hostname.endsWith("." + elakayHost)
    );
  } catch {
    return false;
  }
};

// Accepte indifféremment `/deal-today` ou une URL complète. Une URL de la
// même origine est convertie en route React (navigation sans rechargement) ;
// une URL http(s) externe reste un lien standard. Les schémas dangereux sont
// rejetés et deviennent un texte non cliquable.
export const resolveMenuSwiperDestination = (destination) => {
  const raw = typeof destination === 'string' ? destination.trim() : '';
  if (!raw) return { type: 'none', href: '' };
  if (raw.startsWith('/') && !raw.startsWith('//')) return { type: 'internal', href: raw };

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : ELAKAY_SITE_URL;
    const url = new URL(raw, base);
    if (!['http:', 'https:'].includes(url.protocol)) return { type: 'none', href: '' };

    const configuredHost = new URL(ELAKAY_SITE_URL).hostname;
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : configuredHost;
    const isInternal = url.hostname === currentHost || url.hostname === configuredHost || url.hostname.endsWith(`.${configuredHost}`);
    return isInternal
      ? { type: 'internal', href: `${url.pathname}${url.search}${url.hash}` || '/' }
      : { type: 'external', href: url.href };
  } catch {
    return { type: 'none', href: '' };
  }
};

const MenuSwiperLink = ({ item, duplicate = false }) => {
    const destination = resolveMenuSwiperDestination(item.destination);
    const presentation = item.displayStyle === 'card' ? 'card' : 'transparent';
    const manualColors = item.colorMode === 'manual';
    const style = {
        ...(manualColors && item.backgroundColor ? { '--menu-swiper-bg': item.backgroundColor } : {}),
        ...(manualColors && item.textColor ? { '--menu-swiper-color': item.textColor } : {}),
        '--menu-swiper-shadow': item.shadow || '0 4px 12px rgba(0, 75, 99, 0.16)',
        '--menu-swiper-radius': `${Math.min(999, Math.max(0, Number(item.borderRadius) || 0))}px`,
        '--menu-swiper-font-size': `${Math.min(24, Math.max(10, Number(item.fontSize) || 13))}px`,
    };
    const devices = { mobile: true, tablet: true, desktop: true, ...(item.devices || {}) };
    const className = [
      'elakay-subnav__menu-swiper',
      `elakay-subnav__menu-swiper--${presentation}`,
      manualColors ? 'has-manual-colors' : 'has-automatic-colors',
      devices.mobile === false ? 'is-hidden-mobile' : '',
      devices.tablet === false ? 'is-hidden-tablet' : '',
      devices.desktop === false ? 'is-hidden-desktop' : '',
    ].filter(Boolean).join(' ');
    const text = item._resolvedText || resolveMenuSwiperItemText(item);
    if (destination.type === 'internal') return <Link to={destination.href} className={className} style={style} tabIndex={duplicate ? -1 : undefined}>{text}</Link>;
    if (destination.type === 'external') return <a href={destination.href} className={className} style={style} tabIndex={duplicate ? -1 : undefined}>{text}</a>;
    return <span className={`${className} is-disabled`} style={style}>{text}</span>;
};

// One rendered group must be wider than the visible center lane. Two equal
// groups are then animated edge-to-edge, producing a seamless loop. This is
// deliberately based on measured pixels, never on a fixed clone count.
export const calculateMenuTrackRepeatCount = (viewportWidth, cycleWidth) => {
    const viewport = Number(viewportWidth);
    const cycle = Number(cycleWidth);
    if (!Number.isFinite(viewport) || !Number.isFinite(cycle) || viewport <= 0 || cycle <= 0) return 1;
    return Math.max(1, Math.ceil((viewport + cycle) / cycle));
};

const useMenuTrackMetrics = (contentKey) => {
    const viewportRef = React.useRef(null);
    const measureRef = React.useRef(null);
    const [metrics, setMetrics] = React.useState({ viewportWidth: 0, cycleWidth: 0 });

    React.useLayoutEffect(() => {
        const viewport = viewportRef.current;
        const measure = measureRef.current;
        if (!viewport || !measure) return undefined;
        let frame = 0;
        const read = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const viewportWidth = viewport.getBoundingClientRect().width;
                const cycleWidth = measure.scrollWidth;
                setMetrics((current) => (
                    Math.abs(current.viewportWidth - viewportWidth) < 0.5 && Math.abs(current.cycleWidth - cycleWidth) < 0.5
                        ? current
                        : { viewportWidth, cycleWidth }
                ));
            });
        };
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(read) : null;
        observer?.observe(viewport);
        observer?.observe(measure);
        window.addEventListener('resize', read);
        document.fonts?.ready?.then(read).catch(() => undefined);
        read();
        return () => {
            cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener('resize', read);
        };
    }, [contentKey]);

    return { viewportRef, measureRef, ...metrics };
};

const MenuSliderBar = ({
    infoSource,
    sliderShow,
    drawerClickHander,
    messages,
    categoryTitles = [],
    previousCategoryTitle,
    previousCategoryLink,
    showFilterMenu = true,
    headerOwnsMobileMenu = false,
}) => {
    // if (!sliderShow) return null;

    const defaultMessages = [
        elakayAutoText("h_c528229bfd1a68", "New arrivals every week"),
        elakayAutoText("h_322a972eb1879a", "Enjoy today's special offers"),
        elakayAutoText("h_831028d1804d75", "Discover local shops"),
    ];

    // Chaque message du ticker peut être une simple chaîne (titres de
    // catégorie, messages traduits par défaut) ou un objet { text, color }
    // (messages configurés depuis AnimationSettings). On normalise pour un
    // rendu uniforme.
    const normalizeSlide = (item) =>
        typeof item === "string" ? { text: item, color: null } : { text: item?.text, color: item?.color || null };

    const menuSettings = infoSource?.allSettings?.menuSliderBar || {};
    const hasUnifiedItems = Array.isArray(menuSettings.swiperItems);
    const legacyMessages = Array.isArray(infoSource?.allSettings?.subnav?.messages)
        ? infoSource.allSettings.subnav.messages
        : [];
    const fallbackMessages = messages?.length
        ? messages
        : (hasUnifiedItems || legacyMessages.length ? [] : defaultMessages);

    const slides = [
        previousCategoryTitle,
        ...categoryTitles,
        ...fallbackMessages,
    ]
        .map(normalizeSlide)
        .filter((slide) => slide.text);

    const location = useLocation();
    const navigate = useNavigate();
    const goBack = goBackWith(navigate);
    const pageContext = resolveMenuSliderPageContext(location.pathname);
    const legacyMenus = Array.isArray(menuSettings.swiperMenus) ? menuSettings.swiperMenus : [];
    const unifiedLegacyItems = [
        ...legacyMessages.map((message) => ({
            text: typeof message === 'string' ? message : message?.text,
            destination: '',
            displayStyle: 'transparent',
            colorMode: (typeof message === 'object' && message?.color) ? 'manual' : 'automatic',
            textColor: typeof message === 'object' ? message?.color : null,
            enabled: true,
            devices: { mobile: true, tablet: true, desktop: true },
        })),
        ...legacyMenus,
    ];
    // Page exception > item placement > every page (items saved before page
    // targeting existed have no placement and therefore show everywhere).
    const pageResolution = resolveMenuSliderItems(
        hasUnifiedItems ? menuSettings.swiperItems : unifiedLegacyItems,
        menuSettings.pageOverrides,
        pageContext
    );
    const buttonVisibility = resolveMenuSliderButtons(pageResolution.buttons, {
        back: true,
        filter: showFilterMenu,
        quickMenu: menuSettings.showMobileQuickMenu ?? true,
    });
    // The Header renders its own mobile shortcuts: never a second copy here.
    if (headerOwnsMobileMenu) buttonVisibility.quickMenu = false;
    const swiperMenus = (menuSettings.showSwiperMenus ?? true)
        ? pageResolution.items
            .map((item) => ({ ...item, _resolvedText: resolveMenuSwiperItemText(item) }))
            .filter((item) => item?.enabled !== false && item._resolvedText.trim() && canDisplayMenuSwiperItem(item, infoSource))
        : [];
    const hasMobileSwiperItems = swiperMenus.some((item) => item?.devices?.mobile !== false);
    const hasTabletSwiperItems = swiperMenus.some((item) => item?.devices?.tablet !== false);
    const hasDesktopSwiperItems = swiperMenus.some((item) => item?.devices?.desktop !== false);

    // Couleur de texte par défaut du ticker — configurable depuis
    // AnimationSettings. Utilisée pour les messages sans couleur propre.
    const defaultTextColor = infoSource?.allSettings?.subnav?.defaultTextColor;

    // The source sequence stays unique. ResizeObserver measures this exact
    // sequence and the available center lane; only visual copies are made.
    const sourceTrackItems = [
        ...slides.map((slide, index) => ({ kind: 'message', slide, sourceIndex: index })),
        ...swiperMenus.map((item, index) => ({ kind: 'menu', item, sourceIndex: index })),
    ];
    const contentKey = JSON.stringify(sourceTrackItems.map((entry) => entry.kind === 'message'
        ? ['message', entry.slide?.text, entry.slide?.color]
        : ['menu', entry.item?._resolvedText, entry.item?.destination, entry.item?.fontSize, entry.item?.displayStyle, entry.item?.devices, entry.item?.audience]));
    const { viewportRef, measureRef, viewportWidth, cycleWidth } = useMenuTrackMetrics(contentKey);
    const repeatSwiperMenus = menuSettings.repeatSwiperMenus ?? true;
    const repeatCount = calculateMenuTrackRepeatCount(viewportWidth, cycleWidth);
    const repeatedGroup = Array.from({ length: repeatCount }, (_, repetition) =>
        sourceTrackItems.map((entry) => ({ ...entry, repetition }))
    ).flat();
    const ticker = useElakaySwiper("subnavTicker", {
        slideCount: sourceTrackItems.length,
        originPatch: {
            effect: { type: 'slide', direction: 'horizontal', speed: 6500, loop: sourceTrackItems.length > 0 },
            layout: { slidesPerView: 'auto', spaceBetween: 0, slidesPerGroup: 1 },
            autoplay: { enabled: sourceTrackItems.length > 0, delay: 0, disableOnInteraction: false, pauseOnMouseEnter: false },
            touch: { allowTouchMove: true, simulateTouch: true },
        },
    });
    const configuredSpeed = Number(ticker.resolved?.effect?.speed) || 6500;
    const pixelsPerSecond = Math.min(80, Math.max(24, 300000 / configuredSpeed));
    const groupWidth = cycleWidth > 0 ? cycleWidth * repeatCount : 0;
    const trackDuration = groupWidth > 0 ? Math.max(8, groupWidth / pixelsPerSecond) : 20;

    const renderTrackEntry = (entry, key, duplicate) => entry.kind === 'message' ? (
        <span key={key} className="elakay-subnav__slide" aria-hidden={duplicate ? 'true' : undefined} style={entry.slide.color || defaultTextColor ? { color: entry.slide.color || defaultTextColor } : undefined}>{entry.slide.text}</span>
    ) : (
        <span key={key} className="elakay-subnav__slide elakay-subnav__slide--menu" aria-hidden={duplicate ? 'true' : undefined}><MenuSwiperLink item={entry.item} duplicate={duplicate} /></span>
    );

    const backLabel  = elakayAutoText("h_e84712fbf4eee8", "Go back");
    const safeBackLink = isInternalElakayPath(previousCategoryLink) ? previousCategoryLink : null;

    // Couleur des boutons retour/filtre — configurable depuis AnimationSettings
    // (page "Barre de sous-navigation"). Les deux boutons partagent toujours
    // les mêmes valeurs pour rester visuellement identiques.
    const accentBackground = infoSource?.allSettings?.menuSliderBar?.accentBackground;
    const accentColor      = infoSource?.allSettings?.menuSliderBar?.accentColor;
    const accentStyle = (accentBackground || accentColor)
        ? {
            ...(accentBackground ? { background: accentBackground } : {}),
            ...(accentColor ? { color: accentColor } : {}),
        }
        : undefined;

    // Fond du bandeau entier (derrière boutons + ticker) — configurable
    // depuis AnimationSettings (section "Barre de sous-navigation").
    const barBackground = infoSource?.allSettings?.menuSliderBar?.backgroundColor;
    const animationDuration = Math.min(500, Math.max(120, Number(menuSettings.mobileAnimationDuration) || 220));
    const staggerDelay = Math.min(150, Math.max(0, Number(menuSettings.mobileStaggerDelay) || 40));
    const barStyle = {
        ...(barBackground ? { background: barBackground } : {}),
        ...(accentBackground ? { '--subnav-accent-bg': accentBackground } : {}),
        ...(accentColor ? { '--subnav-accent-color': accentColor } : {}),
        '--subnav-mobile-duration': `${animationDuration}ms`,
        '--subnav-mobile-stagger': `${staggerDelay}ms`,
    };
    const mobileItems = buildMobilePrimaryNavigation(infoSource);
    const mobileIcon = { deal: FaTags, step: GrAscend, center: FaChartLine, shop: FaStore };
    const barClasses = [
        'elakay-subnav',
        menuSettings.enableBarAnimation ? 'has-bar-animation' : 'no-bar-animation',
        menuSettings.enableButtonAnimations ? 'has-button-animations' : 'no-button-animations',
        menuSettings.enableIconAnimations ? 'has-icon-animations' : 'no-icon-animations',
        menuSettings.enableMenuAnimations ? `has-menu-animations animation-${menuSettings.mobileAnimationType || 'fade-up'}` : 'no-menu-animations',
        menuSettings.enableHoverAnimations ? 'has-hover-animations' : 'no-hover-animations',
        hasMobileSwiperItems ? 'has-mobile-swiper-items' : '',
        hasTabletSwiperItems ? 'has-tablet-swiper-items' : '',
        hasDesktopSwiperItems ? 'has-desktop-swiper-items' : '',
    ].join(' ');

    return (
        <div className={barClasses} style={barStyle}>
            {!buttonVisibility.back ? null : safeBackLink ? (
                <Link
                    to={safeBackLink}
                    className="elakay-subnav__btn elakay-subnav__back"
                    aria-label={backLabel}
                    style={accentStyle}
                >
                    <FaLongArrowAltLeft />
                </Link>
            ) : (
                <>
                <button
                    type="button"
                    className="elakay-subnav__btn elakay-subnav__back"
                    onClick={goBack}
                    aria-label={backLabel}
                    style={accentStyle}
                >
                    <FaLongArrowAltLeft />
                </button>
                </>
            )}

            {sourceTrackItems.length > 0 && (
                <div ref={viewportRef} className="elakay-subnav__ticker" aria-label={elakayAutoText("h_subnav_links_and_messages", "Navigation et informations")}>
                    <div ref={measureRef} className="elakay-subnav__measure" aria-hidden="true">
                        {sourceTrackItems.map((entry, index) => renderTrackEntry(entry, `measure-${index}`, true))}
                    </div>
                    {(cycleWidth <= 0 || !repeatSwiperMenus) && <div className={`elakay-subnav__track-group ${cycleWidth <= 0 ? 'elakay-subnav__track-group--fallback' : 'elakay-subnav__track-group--static'}`}>
                        {sourceTrackItems.map((entry, index) => renderTrackEntry(entry, `fallback-${index}`, false))}
                    </div>}
                    {cycleWidth > 0 && repeatSwiperMenus && <div className="elakay-subnav__marquee" style={{ '--subnav-track-width': `${groupWidth}px`, '--subnav-track-duration': `${trackDuration}s` }}>
                        {[0, 1].map((group) => <div key={group} className="elakay-subnav__track-group" aria-hidden={group === 1 ? 'true' : undefined}>
                            {repeatedGroup.map((entry, index) => renderTrackEntry(entry, `group-${group}-${index}`, group === 1 || entry.repetition > 0))}
                        </div>)}
                    </div>}
                </div>
            )}

            {buttonVisibility.quickMenu && (
                <nav className="elakay-subnav__mobile-menu" aria-label={elakayAutoText("h_mobile_quick_navigation", "Quick navigation")}>
                    {mobileItems.map((item, index) => {
                        const Icon = mobileIcon[item.icon] || FaStore;
                        return (
                            <Link
                                key={item.key}
                                to={item.route}
                                className="elakay-subnav__mobile-link"
                                style={{ '--subnav-item-index': index }}
                            >
                                <Icon className="elakay-subnav__mobile-icon" aria-hidden="true" />
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>
            )}

            {buttonVisibility.filter && <button
                type="button"
                className="elakay-subnav__btn elakay-subnav__filter"
                onClick={drawerClickHander}
                aria-label={elakayAutoText("h_d7decf1aa22b02", "Filter")}
                style={accentStyle}
            >
                <FaSlidersH />
            </button>}
        </div>
    );
};

export default MenuSliderBar;
